import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';

import BannerStyleButtonActionAccordion from '../../../components/BannerStyleButtonActionAccordion';
import TextBannerLarge from '../../../components/TextBannerLarge';
import BannerStyleCheckboxListsActionAccordion from '../../../components/BannerStyleCheckboxListsActionAccordion';
import BadgeDivider from '../../../components/BadgeDivider';
import ProductCardListActionAccordion from '../../../components/ProductCardListActionAccordion';
import { BackgroundColor } from '../../../components/Badge';
import TreatmentBannerStyleDownloadButtonActionAccordion from '../../../components/TreatmentBannerStyleDownloadButtonActionAccordion';
import {
  ImmunotherapyStepIds,
  NutritionAdviceStepIds,
  STEP_STATUS,
  STEP_TYPE,
} from '../../constants/treatment';

const TreatmentTab = ({ data, bannerStyleButtonOnClick }) => {
  const {
    steps,
    notForgettingMedicationBanner,
    multipleMedicationsBanner,
    completingJournalBeforeConsultationBanner,
    wantMoreConsultationsBanner,
  } = data.uiData;

  // question for Djordje: should i move this to helpers folder?
  const formatStepNumber = (number) =>
    number < 9 ? `0${number + 1}` : `${number + 1}`;

  const filteredSteps = steps.filter((step) => step != null);

  return filteredSteps.map((step, index) => {
    const isStepInactive = step.status === STEP_STATUS.INACTIVE;
    const isStepActive = step.status === STEP_STATUS.ACTIVE;
    const isStepActiveAndBookingCompleted =
      isStepActive && step.bookingData?.bookingCompleted;
    const isStepCompleted = step.status === STEP_STATUS.COMPLETED;
    const isTagTextVisible = data.treatment?.activeStepId === step.id;
    const tagText = isTagTextVisible ? step.tagText : null;
    const showDivider =
      filteredSteps.length > 5 &&
      (step.id === ImmunotherapyStepIds.BOOK_FIRST_TIME_INTAKE_MEDICATION_ONE ||
        step.id === ImmunotherapyStepIds.BOOK_FIRST_TIME_INTAKE_MEDICATION_TWO);
    // change this when there is no more limit for consultations
    const showWantMoreConsultationsBanner =
      wantMoreConsultationsBanner && filteredSteps.length === 16;

    const roundNumber =
      step.round && filteredSteps.length > 5 ? ` (${step.round})` : '';

    let stepComponent;
    switch (step.type) {
      case STEP_TYPE.BOOKING:
      case STEP_TYPE.LINK:
        stepComponent = (
          <BannerStyleButtonActionAccordion
            activeStateTagText={tagText}
            number={formatStepNumber(index)}
            title={step.title + roundNumber}
            subtitle={step.subtitle}
            text={step.description}
            isOpenByDefault={isStepActive}
            completed={isStepActiveAndBookingCompleted || isStepCompleted}
            bannerTitle={step.buttonData?.label}
            bannerSubtitle={step.buttonData?.text}
            bannerImageUrl={step.buttonData?.imageUrl}
            bannerUrl={step.buttonData?.url}
            bannerActionState={step.buttonData?.state}
            bannerStyleButtonOnClick={() => bannerStyleButtonOnClick(step.tab)}
          />
        );
        break;

      case STEP_TYPE.BUTTON:
        stepComponent = (
          <TreatmentBannerStyleDownloadButtonActionAccordion
            activeStateTagText={tagText}
            treatmentType={data.treatment.type}
            number={formatStepNumber(index)}
            title={step.title + roundNumber}
            subtitle={step.subtitle}
            text={step.description}
            isOpenByDefault={isStepActive}
            completed={isStepCompleted}
            buttonsData={step.buttonsData}
            stepId={step.id}
          />
        );
        break;

      case STEP_TYPE.MEDICATION:
        stepComponent = (
          <BannerStyleCheckboxListsActionAccordion
            activeStateTagText={tagText}
            number={formatStepNumber(index)}
            title={step.title}
            subtitle={step.subtitle}
            text={step.description}
            isOpenByDefault={isStepActive}
            completed={isStepCompleted}
            inactive={isStepInactive}
            checkboxLists={step.checkboxLists}
            treatmentId={data.treatment.id}
            stepId={step.id}
          />
        );

        break;

      case STEP_TYPE.PRODUCT:
        stepComponent = (
          <ProductCardListActionAccordion
            activeStateTagText={tagText}
            number={formatStepNumber(index)}
            title={step.title}
            subtitle={step.subtitle}
            text={step.description}
            isOpenByDefault={isStepActive}
            completed={isStepCompleted}
            productList={step.productList}
            isSingleSelect={step.isSingleSelect}
            urlAttributes={step.urlAttributes}
            isLocked={isStepInactive}
          />
        );
        break;

      default:
        stepComponent = null;
        break;
    }

    return (
      <React.Fragment key={step.id}>
        {showDivider && (
          <BadgeDivider
            key={step.id}
            backgroundColor={BackgroundColor.WHITE}
            text={
              <FormattedMessage
                id={`treatment.treatment-start-${step.medication}`}
                defaultMessage=''
              />
            }
          />
        )}

        {stepComponent}

        {step.id === ImmunotherapyStepIds.BOOK_PREPARATORY_CONSULTATION &&
          multipleMedicationsBanner && (
            <TextBannerLarge
              title={multipleMedicationsBanner.title}
              text={multipleMedicationsBanner.text}
              backgroundColor={multipleMedicationsBanner.backgroundColor}
            />
          )}

        {step.id ===
          ImmunotherapyStepIds.BOOK_FIRST_TIME_INTAKE_MEDICATION_ONE &&
          notForgettingMedicationBanner && (
            <TextBannerLarge
              title={notForgettingMedicationBanner.title}
              text={notForgettingMedicationBanner.text}
              backgroundColor={notForgettingMedicationBanner.backgroundColor}
            />
          )}

        {step.id === NutritionAdviceStepIds.COMPLETE_FOOD_JOURNAL &&
          completingJournalBeforeConsultationBanner && (
            <TextBannerLarge
              title={completingJournalBeforeConsultationBanner.title}
              text={completingJournalBeforeConsultationBanner.text}
              backgroundColor={
                completingJournalBeforeConsultationBanner.backgroundColor
              }
            />
          )}

        {step.id === NutritionAdviceStepIds.RECEIVE_NUTRITION_PLAN_SEVEN &&
          showWantMoreConsultationsBanner && (
            <TextBannerLarge
              title={wantMoreConsultationsBanner.title}
              text={wantMoreConsultationsBanner.text}
              backgroundColor={wantMoreConsultationsBanner.backgroundColor}
            />
          )}
      </React.Fragment>
    );
  });
};

TreatmentTab.propTypes = {
  data: PropTypes.object.isRequired,
  bannerStyleButtonOnClick: PropTypes.func,
};

export default TreatmentTab;
